/**
 * 卫士右侧栏
 */

import { EE, HelperCore } from "@/common/dependencies";
import settingStore from "@/components/chat-ws/setting/store";
import wsStore from "@/components/chat-ws/store";
import chatStoreManager from "@/store/chat/manager";
import slsLogger from "@public/log/sls";
import urm from "@public/urm";
import { makeAutoObservable, runInAction } from "mobx";
import globalStore from "./global";
import userStore from "./user";
import { sendBroadcast } from "@/hooks/useBroadcast";
import appLogger from "@public/log/monitor";

export const SIZE_MODEL_BIG = 1;
export const SIZE_MODEL_SMALL = 0;

class Store {
  module = "360AI";
  sid = "";
  isClient = false; // 是否是卫士客户端
  isLLQ = /NAMIAI/i.test(navigator.userAgent) || localStorage.getItem("helper_platform") == "nami"; // 是否是纳米客户端
  is360se = /QIHU 360SE/i.test(navigator.userAgent);
  isSafeCloud = /ENTCLIENT/i.test(navigator.userAgent)
  isX64 = /x64/i.test(navigator.userAgent);
  supportMic = false; // 是否支持 asr
  sizeModel = SIZE_MODEL_SMALL;
  isFullscreen = false;     // 是否全屏
  title = '';
  version = ""
  hasLogouted = false;  // 由客户端退出登录
  pid = ""; // 客户端 pid，用于定制，默认为 "namiai"
  isToB = false;  // 是否 toB 客户端
  hideSideMenu = false; // toB 客户端，非“信创单机版”，隐藏侧边栏

  checkClientReady = false;
  checkClientReadyCallback = [];
  loginedCallback = true;

  sizeOptions = {
    [SIZE_MODEL_SMALL] : {
      width : 440,
      height : 1000,
    },
    [SIZE_MODEL_BIG] : {
      width : Math.min(1400, Math.floor(window.screen.availWidth * .8)),
      height : Math.min(900, Math.floor(window.screen.availHeight * .8)),
    },
  }

  /**
   * 是否可拖动 banner 移动
   */
  get canDrag () {
    return this.sizeModel == SIZE_MODEL_BIG;
  }

  /**
   * 是否支持数据库
   */
  get supportKnowledge () {
    // 自测调试
    if(HelperCore.IS_LOCAL) {
      return true;
    }
    return this.isLLQ && this.versionGt("1.3.1036.0");
  }
  /**
   * 是否支持网页
   */
  get supportWebpage () {
    return this.isLLQ && (this.versionGt("1.3.1068.63") || (HelperCore.isMac() && this.versionGt("1.3.1036.63")));
  }

  /**
   * 支持浏览器智阅预览
   */
  get supportEyunPlugin () {
    return this.isLLQ
      && !this.isToB
      && (
        (!HelperCore.isMac() && this.versionGt("1.3.1132.0"))
        || (HelperCore.isMac() && this.versionGt("1.3.1176.0"))
      )
  }

  /**
   * 支持 Mcp 智能体
   */
  get supportMcpAgent () {
    return this.isSeLLQ || (this.isLLQ
      && !this.isToB
      && (
        (!HelperCore.isMac() && this.versionGt("1.3.1201.0"))
        || (HelperCore.isMac() && this.versionGt("1.3.1201.0"))
      )
    )
  }

  // 360se支持mcp
  get isSeLLQ () {
    return this.is360se &&
      (
        (
          !this.versionLt("16.1.2082.64") &&
          this.isX64
        ) ||
        (
          !this.versionLt("16.0.2082.0") &&
          !this.isX64
        )
      )
  }

  /**
   * 不支持 win7 操作系统
   */
  get notSupportWin7 () {
    // 如果是 win7 系统，返回 true，否则返回 false
    const { name, version } = HelperCore.getOsInfo()
    if (version === 'Windows 7') {
      return true
    }
    return false
  }

  /**
   * 支持调起 BU
   */
  get supportOpenBu () {
    return this.isLLQ
      && !this.isToB
      && (
        (!HelperCore.isMac() && this.versionGt("1.3.1233.0"))
        || (HelperCore.isMac() && this.versionGt("1.3.1233.0"))
      )
  }

  /**
   * 支持浏览器截图
   */
  get supportScreenShot () {
    // 安全云纳米客户端暂时不支持截图
    if (this.isSafeCloud) {
      return false
    }
    return this.isLLQ
      && !this.isToB
      && (
        (!HelperCore.isMac() && this.versionGt("1.3.1146.0"))
        || (HelperCore.isMac() && this.versionGt("1.3.1282.63") || HelperCore.IS_LOCAL)
      )
  }

  constructor () {
    makeAutoObservable(this);
    this.init();

    if(!HelperCore.IS_PROD){
      window._client = this;
    }
  }

  /**
   * 是否在加速球内
   */
  checkClient () {
    // 是否在加速球内
    this.run("IsAssistantClient", res => {
      runInAction(() => {
        this.isClient = res == 1;

        if(this.isClient){
          HelperCore.addClass(document.body, "page-client");
          urm.updateBaseParams({
            source : "weishi",
          });

          if(location.pathname != "/ws"){
            this.sizeModel = SIZE_MODEL_BIG;
          }
          else {
            globalStore.setTheme("light");
          }

          this.run("IsSupportMic", res => {
            runInAction(() => {
              this.supportMic = res == 1;
            });
          });
          this.run("GetCookie", cookie => {
            // debug
            HelperCore.log("GetCookie", cookie);

            if(cookie){
              const [_Q, _T, _S] = cookie.split("|");
              const Q = decodeURIComponent(_Q);
              const T = decodeURIComponent(_T);
              if(Q && T){
                if(Q != HelperCore.getCookie("Q")){
                  HelperCore.log("GetCookie 写入 cookie");
                  HelperCore.setCookieWithSamesite("Q", Q, null, { path : "/" });
                  HelperCore.setCookieWithSamesite("T", T, null, { path : "/" });
                }
              }
              else {
                HelperCore.log("GetCookie 清除 cookie");
                HelperCore.setCookie("Q", "", -1, { path : "/" });
                HelperCore.setCookie("T", "", -1, { path : "/" });
              }

            }

            runInAction(() => {
              this.checkClientReady = true;
              this.doCheckClientReady();
            });
          });
          this.startTimer();
        }

        // 量较大，先不加
        // this.log({
        //   arrt : "IsAssistantClient",
        //   ext : JSON.stringify(res),
        // });
      });
    });

    const urlParams = HelperCore.getParams();
    if(!HelperCore.IS_PROD){
      if(urlParams.debug == "test_client" || localStorage.getItem("test_client") == 1){
        this.isClient = true;
        // this.supportMic = true;
        urm.updateBaseParams({
          source : "weishi",
        });
        this.checkClientReady = true;
        this.doCheckClientReady();
        this.startTimer();
        HelperCore.addClass(document.body, "page-client");

        if(location.pathname != "/ws"){
          this.sizeModel = SIZE_MODEL_BIG;
        }
        else {
          globalStore.setTheme("light");
        }
      }
    }

    if (this.isLLQ || this.is360se) {
      // 监听登录状态变化
      window.loginStatusUpdate = (QT) => {
        if(!!QT) {
          userStore.getCoinBalance()
          userStore.getMembershipInfo()
          userStore.getMembershipUsage()
        }

        const callback = this.loginedCallback
        const type = typeof callback;
        if(type == "function" && !!QT){
          userStore.requestUserInfo();
          callback();
          return
        }

        if(location.pathname == "/tools/pspic") {
          EE.emit("toolsLoginStatusUpdate", {
            logined : !!QT,
          });
          return
        }

        HelperCore.log('Get Q&T from 360SE:', QT);
        EE.emit("loginStatusUpdate", {
          logined : !!QT,
        });

        this.log({
          arrt : "loginStatusUpdate",
          ext : QT ? "login" : "logout",
          ext1 : String(QT || "").slice(0, 10),
        });

        setTimeout(() => {
          sendBroadcast("user-status-changed");
          globalStore.reloadPage();
        }, 0);
      };
    }

    // 是否为全屏模式
    this.run("IsMaxWnd", res => {
      runInAction(() => {
        this.isFullscreen = res == 1;
      });
    });

    // 侧边栏/窗口
    this.run("GetWndMode", res => {
      runInAction(() => {
        if([SIZE_MODEL_BIG, SIZE_MODEL_SMALL].includes(res)){
          this.sizeModel = res;
        }
      })
    });

    // 桌面工作区大小
    // 与 window.screen 一致，不需要调用本接口
    this.run("GetWorkArea", res => {
      // res: "0,0,1280,680"
      HelperCore.log(`客户端 GetWorkArea 返回`, res);
    });
  }

  whenCheckClientReady (fn = () => {}) {
    if(this.checkClientReady){
      try{
        fn();
      } catch(e){}
    }
    else {
      this.checkClientReadyCallback.push(fn);
    }
  }
  doCheckClientReady () {
    HelperCore.log("doCheckClientReady", this.checkClientReadyCallback.length);

    if(this.checkClientReadyCallback.length){
      this.checkClientReadyCallback.forEach(fn => {
        try{
          fn();
        } catch(e){}
      });
      this.checkClientReadyCallback = [];
    }
  }

  /**
   * 调用卫士客户端接口
   * @param {String} eventName
   * @param {?Funtion} cb
   * @param {?String|Object} param1
   * @param {?String|Object} param2
   * @example
   *    run("xxx")
   *    run("xxx", () => {})
   *    run("Show", () => {}, 600, 400)
   */
  run (eventName, cb, param1 = "", param2 = "", module = this.module) {
    cb = cb || (() => {});
    param1 = typeof param1 == "string" ? param1 : JSON.stringify(param1);
    param2 = typeof param2 == "string" ? param2 : JSON.stringify(param2);

    // debug
    // HelperCore.log(`调用客户端接口`, eventName, param1, param2, module);

    console.log(`调用客户端接口`, eventName, param1, param2, module);
    try{
      window.external.AppCmd(this.sid, module, eventName, param1, param2, (status, data, ...params) => {
        // HelperCore.log(`客户端接口 ${eventName} 回调`, data, ...params);
        console.log(`客户端接口 ${eventName} 回调`, status, data, ...params);
        try{
          cb(JSON.parse(data), ...params);
        } catch(e){
          console.error(`客户端接口回调 parse error:`, e);
          cb(data, ...params);
        }
      });
    } catch(e){}
  }

  /**
   * 切换大小窗
   * @param {String} sizeModel
   */
  changeSize (sizeModel) {
    if(this.sizeModel == sizeModel){
      return;
    }
    // 应该先退出全屏
    if(this.isFullscreen){
      this.setFullscreen(false);
    }

    this.sizeModel = sizeModel;
    const sizeOption = this.sizeOptions[sizeModel];

    this.log({
      arrt : "changeSize",
      ext : sizeModel,
    });

    // 侧边栏不支持 Dark
    if(this.sizeModel == SIZE_MODEL_SMALL){
      globalStore.setTheme("light");
    }
    else {
      globalStore.checkTheme();
    }

    this.run("SetWndMode", null, sizeModel);
    // 切换窗口模式时设置尺寸
    if(sizeModel == SIZE_MODEL_BIG && sizeOption){
      this.run("Show", null, sizeOption.height, sizeOption.width);

      if(wsStore.currentId && userStore.isUser){
        globalStore.navigate(`/chat/${wsStore.currentId}?src=weishi_web`);
      }
      else {
        globalStore.navigate(`/?src=weishi_web`);
      }
    }
    // 切换侧边栏模式不设置尺寸
    else {
      globalStore.navigate("/ws?src=weishi");
    }
  }

  /**
   * 切换全屏模式
   * @param {Boolean} isFullscreen
   */
  setFullscreen (isFullscreen) {
    this.isFullscreen = isFullscreen;
    if(isFullscreen){
      this.run("MaxWnd");
    }
    else {
      this.run("RestoreWnd");
    }
  }

  /**
   * 浏览器打开页面
   * @param {String} url
   * @param {Boolean} isAI
   */
  openPage (url) {
    window.open(url);
  }
  supportOpenPage () {
    return this.isLLQ && this.versionGt("1.3.1036.0");
  }

  init () {
    try{
      this.sid = window.external.GetSID(window);
    } catch(e){
      HelperCore.log(`获取客户端 sid 失败`);
    }
    this.checkClient();
    this.getVersion();
    this.getPid();
    this.getToB();
    this.addEventListener();
  }

  getVersion () {
    if(this.isLLQ || this.is360se){
      try{
        this.version = window.external.GetVersion(this.sid);
        slsLogger.setBaseData({
          version_code : this.version,
        });
      } catch(e){
        HelperCore.log(`获取客户端版本号失败`, e);

        if(HelperCore.IS_LOCAL){
          // this.version = "1.0.0.0";       // 模拟低版本
          this.version = "1.3.1201.64";   // 模拟当前版本
          // this.version = "8.8.8888.88";   // 模拟高版本
        }
      }
      return;
    }

    this.run("GetVersion", (version) => {
      this.version = version
    })
  }

  getPid () {
    if(this.isLLQ){
      this.run("onGetBrowserPid", (pid) => {
        HelperCore.log("onGetBrowserPid result", pid);

        if(pid){
          this.pid = pid;

          const urlParams = HelperCore.getParams();
          if(urlParams.src){
            appLogger.setBaseData({
              src : `${urlParams.src}-${pid}`,
            });
          }
        }
      }, "", "", "");
    }
  }

  getToB () {
    if(this.isLLQ){
      try{
        if(window.external.Is360()){
          this.isToB = true;

          this.hideSideMenu = typeof window.external.EntPrivateAppCmd == "function";
        }
      } catch(e){}
    }

    // debug
    // if(HelperCore.IS_LOCAL){
    //   this.isToB = true;
    //   this.hideSideMenu = true;
    // }
  }

  /**
   * 代理请求
   * @param {Object} param2
   */
  proxyRequest (eventName, cb, param1, param2, module){
    const callbackName = `proxy_${HelperCore.uniqueId()}`;
    const beginTime = +new Date;

    slsLogger.log({
      key : "proxy",
      action : "request",
      arrt : eventName,
      ext : JSON.stringify(param1),
      ext3 : callbackName,
    });

    window[callbackName] = (...params) => {
      // debug
      HelperCore.log(`proxyRequest ${eventName} 调用 ${param1.api} 接口返回:`, ...params);

      try{
        slsLogger.log({
          key : "proxy",
          action : "response",
          arrt : eventName,
          ext : JSON.stringify(param1),
          ext2 : JSON.stringify(params[0]),
          ext3 : callbackName,
          count : +new Date - beginTime,
        });
      } catch(e){}

      try{
        cb(...params);
      } catch(e){}
      window[callbackName] = null;
      try{
        delete(window[callbackName]);
      } catch(e){}
    }

    if(!param1 || typeof param1 != "object"){
      console.error(`proxyRequest param error`, param1);
      return false;
    }

    param1 = {
      ...param1,
      ...{
        callback : callbackName,
      },
    }
    this.run(eventName, null, JSON.stringify(param1), param2, module);

    if(HelperCore.IS_LOCAL){
      if(/^\/api\/tools\/batch/.test(param1.api)){
        window[callbackName]({
          succ : 200,
          data : JSON.stringify({
            code : 0,
            data : [],
          }),
        });
      }
      else if(/^\/api\/server\/install\/batch/.test(param1.api)){
        const serverNames = param1.param.server_name.split(",");
        const waitTime = 1
          || HelperCore.getRnd(500, 5000);
        if(param1.timeout && waitTime <= param1.timeout){
          setTimeout(() => {
            window[callbackName]({
              succ : 200,
              data : JSON.stringify({
                code : 0,
                data : serverNames.map(serverName => {
                  return {
                    server_name : serverName,
                    installed : true,
                  }
                })
              }),
            });
          }, waitTime);
        }
      }
      else if(/^\/api\/server\/status/.test(param1.api) && param1.method == 1){
        const serverNames = HelperCore.getParams(param1.api).server_name.split(",");
        const waitTime = 0
          || HelperCore.getRnd(500, 5000);
        setTimeout(() => {
          window[callbackName]({
            succ : 200,
            data : JSON.stringify({
              code : 0,
              data : serverNames.map(serverName => {
                return {
                  server_name : serverName,
                  status : 2,
                }
              }),
            }),
          });
        }, waitTime);
      }
      else if(/^\/api\/server\/status/.test(param1.api) && param1.method == 0){
        const serverNames = param1.param.server_names;
        const waitTime = 1
          || HelperCore.getRnd(500, 5000);
        setTimeout(() => {
          window[callbackName]({
            succ : 200,
            data : JSON.stringify({
              code : 0,
              data : serverNames.map(serverName => {
                return {
                  server_name : serverName,
                  installed : true,
                }
              }),
            }),
          });
        }, waitTime);
      }
    }
  }
  proxyRequestAsync (eventName, param1, param2, module){
    return new Promise((resolve, reject) => {
      const res = this.proxyRequest(eventName, (res) => {
        resolve(res);
      }, param1, param2, module);
      if(res === false){
        reject();
      }
    });
  }
  proxyRequestAsyncWithTimeout (eventName, param1, param2, module, rate = 1){
    if(!param1?.timeout){
      return this.proxyRequestAsync (eventName, param1, param2, module);
    }

    const {
      timeout,
      api,
    } = param1;
    return new Promise((resolve, reject) => {
      let timer_timeout = null;
      const res = this.proxyRequest(eventName, (res) => {
        clearTimeout(timer_timeout);
        resolve(res);
      }, param1, param2, module);
      if(res === false){
        reject();
        return;
      }

      const time = timeout * rate;
      const beginTime = +new Date;
      timer_timeout = setTimeout(() => {
        slsLogger.log({
          key : "proxy",
          action : "timeout",
          arrt : eventName,
          ext : JSON.stringify(param1),
          count : +new Date - beginTime,
        });

        reject(`timeout ${time} ${api}`);
      }, time);
    });
  }

  /**
   * 客户端固定回调
   */
  addEventListener () {
    window.onUserInfoChanged = (userInfo) => {
      this.log({
        arrt : "onUserInfoChanged",
        ext : JSON.stringify(userInfo),
        ext2 : HelperCore.getCookie("Q"),
      });
      const isLogin = !(!userInfo || userInfo == 0);

      HelperCore.log(`客户端回调 onUserInfoChanged`, userInfo);

      // 登出时切回侧边栏状态并刷新
      // 不然大小窗状态混乱
      // if(!isLogin){
      //   this.changeSize(SIZE_MODEL_SMALL);
      // }

      setTimeout(() => {
        HelperCore.log(`刷新页面`);
        location.reload();
      }, 500);
    }

    // 全屏回调
    window.onWndMaxed = () => {
      HelperCore.log(`客户端回调 onWndMaxed`);
      runInAction(() => {
        this.isFullscreen = true;
      });
    }
    window.onWndRestored = () => {
      HelperCore.log(`客户端回调 onWndRestored`);
      runInAction(() => {
        this.isFullscreen = false;
      });
    }

    // 划词回调
    window.onSlipperySearch = (params) => {
      this.log({
        arrt : "onSlipperySearch",
        query : JSON.stringify(params),
      });
      const { type, content, b_send } = params;
      HelperCore.log(`客户端回调 onSlipperySearch`, type, content);

      // 关闭设置弹窗
      if(settingStore.open){
        settingStore.hide();
      }

      if (this.sizeModel == SIZE_MODEL_SMALL && this.isClient) {
        if (wsStore.currentStore) {
          wsStore.currentStore.handleSlipperySearch(String(type), content, b_send === false);
          return;
        }
      }

      // 使用普通网页逻辑兜底
      if(!/^\/chat/.test(location.pathname)){
        globalStore.navigate("/chat");
        setTimeout(() => {
          chatStoreManager.current.handleSlipperySearch(String(type), content, b_send === false);
        }, 30);
      }
      else {
        chatStoreManager.current.handleSlipperySearch(String(type), content, b_send === false);
      }
    }
  }

  setData(key, value){
    runInAction(() => {
      this[key] = value;
    })
  }

  startTimer = () => {
    // n 分钟
    const time = 10 * 60 * 1000;
    const timer = setInterval(() => {
      // 低于最低版本，且是隐藏状态，自动刷新
      if(HelperCore.revision.time && HelperCore.revision.time < globalStore.minClientVersion){
        this.run("IsOnEdge")
        .then(res => {
          if(res == 1){
            this.log({
              arrt : "autoreload",
              ext : HelperCore.revision.time,
              ext1 : globalStore.minClientVersion,
            });

            setTimeout(() => {
              location.reload();
            }, 300);
          }
        })
        .catch(res => {});
      }
    }, time);
  }

  GetLoginSection () {
    return new Promise((resolve, reject) => {
      this.run("GetLoginSection", resolve, '', '', 'loginenrol');
    });
  }
  IsLogin () {
    return new Promise((resolve, reject) => {
      this.run("IsLogin", (qt) => {
        resolve(qt == 1);
      }, '', '', 'loginenrol');
    });
  }
  SyncLoginCookie (origin) {
    return new Promise((resolve, reject) => {
      this.run("SyncLoginCookie", resolve, origin, '', 'loginenrol');
    });
  }

  log (params = {}) {
    const baseParams = {
      action : "weishi",
    }
    if(!params.conversation_id && location.pathname == "/ws"){
      try{
        baseParams.conversation_id = wsStore.currentId;
      } catch(e){}
    }

    slsLogger.log({
      ...baseParams,
      ...params,
    });
  }

  /**
   * 判断客户端版本号是否高于某个版本
   * @param {String} version
   */
  versionGt (version) {
    let ret = false

    if(this.version && version) {
      const vcParse = this.version.split(".")
      const versionParse = version.split(".")

      for(let i = 0; i < versionParse.length; i++) {
        const subvc = vcParse[i] || "0"
        const subv = versionParse[i] || "0"

        if(Number(subv) < Number(subvc)) {
          ret = true
          break;
        }

        if(Number(subv) > Number(subvc)) {
          ret = false
          break;
        }
      }
    }

    return ret
  }

  /**
   * 判断客户端版本号是否低于某个版本
   * @param {String} version
   */
  versionLt (version) {
    if(!this.version){
      return true;
    }

    let ret = false;

    if(this.version && version) {
      const vcParse = this.version.split(".")
      const versionParse = version.split(".")

      for(let i = 0; i < versionParse.length; i++) {
        const subvc = vcParse[i] || "0"
        const subv = versionParse[i] || "0"

        if(Number(subv) > Number(subvc)) {
          ret = true
          break;
        }

        if(Number(subv) < Number(subvc)) {
          ret = false
          break;
        }
      }
    }

    return ret
  }
}

const client = new Store();
export default client;
